import { useEffect, useMemo, useState } from "react";
import {
  useCreateUpload,
  useDataImport,
  useImportPreview,
  useImportTransition,
  putFileToSignedUrl,
} from "../hooks";
import type { MappingDraft } from "@/types/data-import.types";

const DRAFT_INICIAL: MappingDraft = {
  sheetName: null,
  headerRow: 1,
  dateColumn: null,
  columnMapping: {},
  conflictPolicy: "IGNORE",
  originRules: [],
};

/**
 * Spec 082 — a máquina de estados do assistente de importação, fora do componente pelo teto de 150
 * linhas do orquestrador. O passo é derivado do STATUS que veio do servidor, nunca de um contador
 * local: recarregar a página no meio precisa cair no mesmo lugar.
 */
export function useImportWizard() {
  const [file, setFile] = useState<File | null>(null);
  const [lineId, setLineId] = useState("");
  const [importId, setImportId] = useState<string | null>(null);
  const [draft, setDraft] = useState<MappingDraft>(DRAFT_INICIAL);

  const create = useCreateUpload();
  const transition = useImportTransition();
  const importQuery = useDataImport(importId ?? "", !!importId);
  const imp = importQuery.data;
  const previewQuery = useImportPreview(imp && imp.status !== "PENDING" ? importId : null, {
    sheetName: draft.sheetName,
    headerRow: draft.headerRow,
  });
  const preview = previewQuery.data;

  // Um perfil que casa traz aba, cabeçalho, coluna do dia e mapeamento prontos (FR-005). Só
  // preenche enquanto o gestor não mexeu em nada: sobrescrever o que ele digitou seria pior do que
  // não sugerir. `applied` guarda o id em vez de um booleano para que trocar de arquivo, e portanto
  // de perfil, volte a sugerir.
  const [appliedProfileId, setAppliedProfileId] = useState<string | null>(null);
  useEffect(() => {
    const p = preview?.suggestedProfile;
    if (!p || appliedProfileId === p.id) return;
    if (Object.keys(draft.columnMapping).length > 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- aplica o perfil sugerido quando a pré-visualização chega
    setDraft({
      sheetName: p.sheetName,
      headerRow: p.headerRow,
      dateColumn: p.dateColumn,
      columnMapping: p.mapping,
      conflictPolicy: "IGNORE",
      // O perfil guarda as ligações junto com o mapeamento: reaplicá-lo sem elas faria o gestor
      // redeclarar a cadeia a cada arquivo, que é justamente o trabalho que o perfil evita.
      originRules: p.originRules,
    });
    setAppliedProfileId(p.id);
  }, [preview, draft.columnMapping, appliedProfileId]);

  const trackingError = importId
    ? (importQuery.isError && importQuery.error) ||
      (previewQuery.isError && previewQuery.error) ||
      null
    : null;

  function retryTracking() {
    if (importQuery.isError) void importQuery.refetch();
    if (previewQuery.isError) void previewQuery.refetch();
  }

  function patchDraft(patch: Partial<MappingDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  async function upload() {
    if (!file) return;
    const result = await create.mutateAsync({ filename: file.name, lineId: lineId || undefined });
    await putFileToSignedUrl(result.uploadUrl, file);
    await transition.context.mutateAsync(result.import.id);
    setImportId(result.import.id);
  }

  async function saveMapping(saveAsProfile?: string) {
    if (!importId) return;
    await transition.mapping.mutateAsync({ id: importId, draft, saveAsProfile });
  }

  async function execute() {
    if (!importId) return;
    await transition.validate.mutateAsync(importId);
    await transition.execute.mutateAsync(importId);
  }

  async function revert() {
    if (!importId) return;
    await transition.revert.mutateAsync(importId);
  }

  function reset() {
    setFile(null);
    setLineId("");
    setImportId(null);
    setDraft(DRAFT_INICIAL);
    setAppliedProfileId(null);
  }

  const step = useMemo(() => {
    if (!importId || !imp) return 1;
    if (imp.status === "PENDING") return 1;
    if (imp.status === "MAPPING") return 2;
    if (imp.status === "VALIDATING") return 3;
    return 4;
  }, [importId, imp]);

  return {
    file,
    setFile,
    lineId,
    setLineId,
    imp,
    preview,
    draft,
    patchDraft,
    step,
    trackingError,
    retryTracking,
    isUploading: create.isPending,
    isMutating:
      transition.mapping.isPending || transition.validate.isPending || transition.execute.isPending,
    isReverting: transition.revert.isPending,
    upload,
    saveMapping,
    execute,
    revert,
    reset,
  };
}
